import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Play, Square, Send, Check, Loader2 } from 'lucide-react';
import type { BuildPlan, CompletionCriterion } from '../../types';

interface StepExecutionPanelProps {
  buildPlan: BuildPlan;
  autoApprove: boolean;
  onAutoApproveChange: (value: boolean) => void;
  onStopGeneration: () => void;
  onSendFeedback: (message: string) => void;
  isProcessing: boolean;
}

export function StepExecutionPanel({
  buildPlan,
  autoApprove,
  onAutoApproveChange,
  onStopGeneration,
  onSendFeedback,
  isProcessing,
}: StepExecutionPanelProps) {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasGraph = !!buildPlan.graph;
  const currentIndex = buildPlan.currentStepIndex;
  const totalSteps = buildPlan.steps.length;
  const currentStep =
    currentIndex >= 0 && currentIndex < totalSteps ? buildPlan.steps[currentIndex] : null;

  // Graph-aware: use graph node count for progress
  const graphVerifiedCount = hasGraph
    ? buildPlan.graph!.nodes.filter((n) => n.status === 'verified').length
    : 0;
  const graphTotalNodes = hasGraph ? buildPlan.graph!.nodes.length : 0;
  const currentGraphNode =
    hasGraph && buildPlan.currentNodeId
      ? buildPlan.graph!.nodes.find((n) => n.id === buildPlan.currentNodeId)
      : null;

  const allStepsVerified = hasGraph
    ? buildPlan.graph!.nodes.every((n) => n.status === 'verified')
    : currentIndex >= totalSteps && buildPlan.steps.every((s) => s.status === 'verified');
  const criteria = buildPlan.completionCriteria ?? [];
  const hasPendingCriteria =
    allStepsVerified && criteria.length > 0 && criteria.some((c) => !c.verified);

  // Edit budget is blocked when editCount exceeds the backend threshold (12)
  const isBudgetBlocked = currentStep != null && (currentStep.editCount ?? 0) > 12;

  // Auto-show feedback input when budget is blocked and AI is not processing
  useEffect(() => {
    if (isBudgetBlocked && !isProcessing && !showFeedbackInput) {
      setShowFeedbackInput(true);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [isBudgetBlocked, isProcessing, showFeedbackInput]);

  const handleStop = useCallback(() => {
    onStopGeneration();
    setShowFeedbackInput(true);
    // Focus textarea after it renders
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [onStopGeneration]);

  const handleSendFeedback = useCallback(() => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    onSendFeedback(trimmed);
    setFeedbackText('');
    setShowFeedbackInput(false);
  }, [feedbackText, onSendFeedback]);

  const handleContinue = useCallback(() => {
    if (hasPendingCriteria) {
      onSendFeedback(
        'Continue. All steps are verified — address the remaining completion criteria to finish the build.',
      );
    } else {
      onSendFeedback(
        'Continue. If the current step is fully built, verify and advance to the next step.',
      );
    }
  }, [onSendFeedback, hasPendingCriteria]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendFeedback();
      }
    },
    [handleSendFeedback],
  );

  // Show continue when AI is idle, there's an unfinished step or pending criteria,
  // and the edit budget hasn't been exhausted
  const showContinue =
    !isProcessing &&
    !showFeedbackInput &&
    !isBudgetBlocked &&
    (currentStep?.status === 'building' || hasPendingCriteria);

  return (
    <div className="border-t bg-muted/20">
      <div className="flex items-center gap-3 px-4 py-2 min-h-[48px]">
        {/* Step info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {isBudgetBlocked
              ? `Step ${currentIndex + 1}: ${currentStep!.title} — needs your guidance`
              : hasPendingCriteria
                ? 'All steps verified — completing setup...'
                : hasGraph && currentGraphNode
                  ? `Node ${graphVerifiedCount + 1} of ${graphTotalNodes}: ${currentGraphNode.title}`
                  : currentStep
                    ? `Step ${currentIndex + 1} of ${totalSteps}: ${currentStep.title}`
                    : hasGraph
                      ? `${graphTotalNodes} nodes complete`
                      : `${totalSteps} steps complete`}
          </p>
        </div>

        {/* Stop button (shown when AI is streaming) */}
        {isProcessing && (
          <Button
            variant="destructive"
            size="icon"
            className="h-7 w-7"
            onClick={handleStop}
            aria-label="Stop generation"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Step/node progress dots */}
        <div className="flex items-center gap-1">
          {(hasGraph ? buildPlan.graph!.nodes : buildPlan.steps).map(
            (item: { id: string; title: string; status: string }) => (
              <div
                key={item.id}
                className={`h-2 w-2 rounded-full ${
                  item.status === 'verified'
                    ? 'bg-primary'
                    : item.status === 'building'
                      ? 'ring-2 ring-primary bg-transparent'
                      : 'bg-muted-foreground/30'
                }`}
                title={`${item.title} (${item.status})`}
              />
            ),
          )}
        </div>

        {/* Auto-approve toggle */}
        <div className="flex items-center gap-1.5">
          <Checkbox
            id="auto-approve"
            checked={autoApprove}
            onCheckedChange={(checked) => onAutoApproveChange(checked === true)}
          />
          <Label htmlFor="auto-approve" className="text-xs cursor-pointer">
            Auto
          </Label>
        </div>

        {/* Continue button (shown when AI is idle and step is unfinished) */}
        {showContinue && (
          <Button size="sm" onClick={handleContinue}>
            <Play className="h-3.5 w-3.5 mr-1" />
            Continue
          </Button>
        )}
      </div>

      {/* Status hint explaining why AI paused and what Continue does */}
      {showContinue && (
        <div className="px-4 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {hasPendingCriteria ? (
              'All steps are built. Press Continue to complete the final wiring and setup.'
            ) : (
              <>
                AI paused between tool calls. Press Continue to keep building
                {' or '}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => {
                    setShowFeedbackInput(true);
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }}
                >
                  give feedback
                </button>
                {' to redirect.'}
              </>
            )}
          </p>
        </div>
      )}

      {/* Completion criteria checklist (shown when all steps verified but criteria pending) */}
      {hasPendingCriteria && (
        <div className="px-4 py-2 border-t border-border/50">
          <div className="space-y-1">
            {criteria.map((criterion: CompletionCriterion) => (
              <div key={criterion.id} className="flex items-center gap-2 text-xs">
                {criterion.verified ? (
                  <Check className="h-3 w-3 text-primary flex-shrink-0" />
                ) : (
                  <Loader2 className="h-3 w-3 text-muted-foreground animate-spin flex-shrink-0" />
                )}
                <span className={criterion.verified ? 'text-muted-foreground' : 'text-foreground'}>
                  {criterion.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback input row (shown after stop) */}
      {showFeedbackInput && !isProcessing && (
        <div className="flex items-end gap-2 px-4 py-2 border-t border-border/50">
          <Textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isBudgetBlocked
                ? 'The AI needs guidance — describe what to try next...'
                : 'Describe a correction...'
            }
            className="min-h-[40px] max-h-[120px] resize-none text-sm"
            rows={1}
          />
          <Button
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={handleSendFeedback}
            disabled={!feedbackText.trim()}
            aria-label="Send feedback"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
